/**
 * Live Trading V3 — hotkey/one-click acknowledgement timing telemetry.
 *
 * The parity requirement is measurable, not vibes: every intent submission
 * records input→guard→local-ack→request→response stamps tagged by input
 * method, in a bounded in-session ring buffer. A controlled parity session
 * reads the summary (P50/P95 per input method per interval, plus how many
 * samples were valid and why the rest were not) from `window.__lt2Timing`
 * alongside the screen recording; nothing here leaves the browser, polls,
 * or renders.
 *
 * CLOCKS (#849, docs/clock-domain-safety.md §3). Every stage carries one
 * `Lt2ClockStamp`: `performance.now()` (same-page monotonic — the ONLY value
 * a duration is computed from), `Date.now()` (correlation with relay/audit
 * epochs — never subtracted for latency), and the page clock generation.
 * The `*Ms` epoch fields on an entry are mirrors of `clocks.*.wallMs`, kept
 * so console readers keep their field names. A stamp is never derived from
 * an epoch; the record API only accepts stamps.
 *
 * Stamp meanings:
 *   inputAt  — the keydown handler entry or the ticket-named eligible mouse
 *              interception boundary. Event.timeStamp is not mixed into this
 *              separately sampled clock domain.
 *   guard    — local preflight ran; the intent is valid or refused.
 *   ack      — the local acknowledgement was presented (pending ack + tone,
 *              or the refusal ack). Parity contract: ack − inputAt ≈ 0.
 *   request  — the order POST left the store.
 *   response — the relay answered (venue outcome known) or the call threw.
 *   render   — the first animation frame AFTER the entry's FIRST
 *              user-visible acknowledgement committed. An acknowledgement-
 *              frame opportunity: not compositor paint, not venue fill.
 *   final    — terminal moment, named by finalOutcome: refusals/throttles at
 *              their ack; rejection/unknown at the response; accepted resting
 *              order when visibility reconciliation settles; accepted taker
 *              at the response.
 */

export interface Lt2ClockStamp {
  /** performance.now() — same-page monotonic. */
  readonly monoMs: number;
  /** Date.now() at the same instant — correlation only. */
  readonly wallMs: number;
  /** Page clock generation at stamp time (see lt2ClockGeneration). */
  readonly gen: number;
}

/** Wall/monotonic disagreement above this is treated as evidence of a clock
 *  event during the interval. Adjacent reads of the two clocks disagree by a
 *  few ms in local probes. The intervals measured here are tens of ms to
 *  ~5 s, so 100 ms leaves room for timer resolution, ordinary oscillator
 *  drift, and normal host clock adjustment without certifying a briefly
 *  paused monotonic clock as good input latency. This policy does not assume
 *  a universal NTP slew-rate cap. Deliberately tighter than the queue lane's
 *  1 s hidden-clock check, which guards a different contract. */
export const LT2_CLOCK_DISAGREEMENT_TOLERANCE_MS = 100;

// The page clock generation moves on every document visibility transition.
// Hidden tabs are throttled and may be suspended; an interval that straddles
// a transition is uncertain and is excluded rather than reported. This is a
// module-level listener by design: the telemetry is module state too.
let _clockGen = 1;
if (typeof document !== 'undefined') {
  document.addEventListener('visibilitychange', () => {
    _clockGen += 1;
  });
}

export function lt2ClockGeneration(): number {
  return _clockGen;
}

export function lt2ClockStamp(): Lt2ClockStamp {
  return { monoMs: performance.now(), wallMs: Date.now(), gen: _clockGen };
}

export type Lt2ExclusionReason =
  | 'missing-start'
  | 'missing-end'
  | 'invalid-value'
  | 'generation-mismatch'
  | 'negative-elapsed'
  | 'wall-ahead-or-paused';

export const LT2_EXCLUSION_REASONS: readonly Lt2ExclusionReason[] = [
  'missing-start',
  'missing-end',
  'invalid-value',
  'generation-mismatch',
  'negative-elapsed',
  'wall-ahead-or-paused',
];

export type Lt2Elapsed =
  | { ok: true; ms: number; wallBackward: boolean }
  | { ok: false; reason: Lt2ExclusionReason };

function validStamp(s: Lt2ClockStamp): boolean {
  return (
    Number.isFinite(s.monoMs) &&
    Number.isFinite(s.wallMs) &&
    Number.isSafeInteger(s.gen) &&
    s.gen > 0
  );
}

/** The single validity rule. Durations come from the monotonic clock only.
 *  Wall ahead of monotonic beyond tolerance is EITHER a forward wall step
 *  (monotonic correct) OR a monotonic clock that paused during suspension
 *  (monotonic under-reports); the browser cannot tell us which, so the
 *  sample is excluded. Wall behind monotonic is treated as a backward wall
 *  adjustment: the monotonic duration remains the accepted value, flagged. */
export function lt2Elapsed(
  start: Lt2ClockStamp | null | undefined,
  end: Lt2ClockStamp | null | undefined,
): Lt2Elapsed {
  if (start == null) return { ok: false, reason: 'missing-start' };
  if (end == null) return { ok: false, reason: 'missing-end' };
  if (!validStamp(start) || !validStamp(end)) return { ok: false, reason: 'invalid-value' };
  if (start.gen !== end.gen) return { ok: false, reason: 'generation-mismatch' };
  const mono = end.monoMs - start.monoMs;
  if (mono < 0) return { ok: false, reason: 'negative-elapsed' };
  const wall = end.wallMs - start.wallMs;
  if (wall - mono > LT2_CLOCK_DISAGREEMENT_TOLERANCE_MS) {
    return { ok: false, reason: 'wall-ahead-or-paused' };
  }
  return { ok: true, ms: mono, wallBackward: mono - wall > LT2_CLOCK_DISAGREEMENT_TOLERANCE_MS };
}

export interface Lt2TimingClocks {
  inputAt: Lt2ClockStamp | null;
  guard: Lt2ClockStamp;
  ack: Lt2ClockStamp;
  request: Lt2ClockStamp | null;
  response: Lt2ClockStamp | null;
  render: Lt2ClockStamp | null;
  final: Lt2ClockStamp | null;
}

export interface Lt2TimingEntry {
  inputMethod: string;
  /** 'pending' = the submission's local ack is on screen and the relay has
   *  not answered yet; patched to a terminal outcome by updateLt2Timing. */
  outcome: 'pending' | 'accepted' | 'rejected' | 'unverified' | 'throttled' | 'guard-refused';
  /** Epoch mirrors of clocks.*.wallMs — correlation with relay/audit rows. */
  inputAtMs: number | null;
  guardMs: number;
  ackMs: number;
  requestMs: number | null;
  responseMs: number | null;
  renderMs: number | null;
  finalMs: number | null;
  finalOutcome: string | null;
  /** Same-page evidence. Durations are computed from these and only these. */
  clocks: Lt2TimingClocks;
}

export interface Lt2TimingRecordInput {
  inputMethod: string;
  outcome: Lt2TimingEntry['outcome'];
  inputAt: Lt2ClockStamp | null;
  guard: Lt2ClockStamp;
  ack: Lt2ClockStamp;
  request: Lt2ClockStamp | null;
  final?: Lt2ClockStamp | null;
  finalOutcome?: string | null;
}

export interface Lt2TimingPatch {
  outcome?: Lt2TimingEntry['outcome'];
  response?: Lt2ClockStamp;
  final?: Lt2ClockStamp;
  finalOutcome?: string | null;
}

const MAX_ENTRIES = 200;
const _entries: Lt2TimingEntry[] = [];
let _entrySeq = 0;
const _entryIds = new WeakMap<Lt2TimingEntry, number>();
const _byId = new Map<number, Lt2TimingEntry>();

export function recordLt2Timing(input: Lt2TimingRecordInput): number {
  const final = input.final ?? null;
  const full: Lt2TimingEntry = {
    inputMethod: input.inputMethod,
    outcome: input.outcome,
    inputAtMs: input.inputAt?.wallMs ?? null,
    guardMs: input.guard.wallMs,
    ackMs: input.ack.wallMs,
    requestMs: input.request?.wallMs ?? null,
    responseMs: null,
    renderMs: null,
    finalMs: final?.wallMs ?? null,
    finalOutcome: input.finalOutcome ?? null,
    clocks: {
      inputAt: input.inputAt,
      guard: input.guard,
      ack: input.ack,
      request: input.request,
      response: null,
      render: null,
      final,
    },
  };
  const id = ++_entrySeq;
  _entries.push(full);
  _entryIds.set(full, id);
  _byId.set(id, full);
  if (_entries.length > MAX_ENTRIES) {
    for (const dropped of _entries.splice(0, _entries.length - MAX_ENTRIES)) {
      const droppedId = _entryIds.get(dropped);
      if (droppedId != null) _byId.delete(droppedId);
    }
  }
  // The render stamp is self-measured: the first animation frame after this
  // record (which every caller writes synchronously with its ack setState)
  // is the frame that could show the acknowledgement.
  scheduleRenderStamp(id);
  return id;
}

function scheduleRenderStamp(id: number): void {
  const raf =
    typeof requestAnimationFrame === 'function'
      ? requestAnimationFrame
      : (fn: () => void) => setTimeout(fn, 0);
  raf(() => {
    const entry = _byId.get(id);
    if (entry && entry.clocks.render == null) {
      const s = lt2ClockStamp();
      entry.clocks.render = s;
      entry.renderMs = s.wallMs;
    }
  });
}

/** Patch a recorded entry with later truth. Unknown or evicted ids are a
 *  silent no-op: a late verify timer must not resurrect or create entries. */
export function updateLt2Timing(id: number, patch: Lt2TimingPatch): void {
  const entry = _byId.get(id);
  if (!entry) return;
  if (patch.outcome != null) entry.outcome = patch.outcome;
  if (patch.finalOutcome !== undefined) entry.finalOutcome = patch.finalOutcome;
  if (patch.response != null) {
    entry.clocks.response = patch.response;
    entry.responseMs = patch.response.wallMs;
  }
  if (patch.final != null) {
    entry.clocks.final = patch.final;
    entry.finalMs = patch.final.wallMs;
  }
}

export function lt2TimingEntries(): readonly Lt2TimingEntry[] {
  return _entries;
}

/** Clears entries only. The page clock generation is a page property and is
 *  left alone so hand-built test stamps stay comparable. */
export function _resetLt2TimingForTests(): void {
  _entries.length = 0;
  _byId.clear();
}

function percentile(sorted: number[], p: number): number | null {
  if (sorted.length === 0) return null;
  const idx = Math.min(sorted.length - 1, Math.ceil((p / 100) * sorted.length) - 1);
  return sorted[Math.max(0, idx)];
}

export interface Lt2IntervalSummary {
  p50Ms: number | null;
  p95Ms: number | null;
  /** Samples the percentiles were computed from. */
  validCount: number;
  /** Retained samples that did not qualify, by reason. Every key present. */
  excluded: Record<Lt2ExclusionReason, number>;
  /** Valid samples whose wall clock stepped backward during the interval. */
  wallBackwardCount: number;
}

function summarizeInterval(
  list: readonly Lt2TimingEntry[],
  pick: (c: Lt2TimingClocks) => [Lt2ClockStamp | null, Lt2ClockStamp | null],
): Lt2IntervalSummary {
  const excluded = Object.fromEntries(
    LT2_EXCLUSION_REASONS.map((r) => [r, 0]),
  ) as Record<Lt2ExclusionReason, number>;
  const valid: number[] = [];
  let wallBackwardCount = 0;
  for (const e of list) {
    const [start, end] = pick(e.clocks);
    const r = lt2Elapsed(start, end);
    if (r.ok) {
      valid.push(r.ms);
      if (r.wallBackward) wallBackwardCount += 1;
    } else {
      excluded[r.reason] += 1;
    }
  }
  valid.sort((a, b) => a - b);
  return {
    p50Ms: percentile(valid, 50),
    p95Ms: percentile(valid, 95),
    validCount: valid.length,
    excluded,
    wallBackwardCount,
  };
}

export interface Lt2TimingSummaryRow {
  inputMethod: string;
  /** Raw entries for the method, valid or not. */
  count: number;
  /** input → local acknowledgement (the parity number). */
  inputToAck: Lt2IntervalSummary;
  /** request → relay response (venue round trip). */
  requestToResponse: Lt2IntervalSummary;
  /** input → first acknowledgement frame (the pending/refusal ack). */
  inputToRender: Lt2IntervalSummary;
  /** input → terminal state (see final). */
  inputToFinal: Lt2IntervalSummary;
  // Flat mirrors — the field names the console read and earlier tests use.
  inputToAckP50Ms: number | null;
  inputToAckP95Ms: number | null;
  requestToResponseP50Ms: number | null;
  requestToResponseP95Ms: number | null;
  inputToRenderP50Ms: number | null;
  inputToRenderP95Ms: number | null;
  inputToFinalP50Ms: number | null;
  inputToFinalP95Ms: number | null;
}

/** Per-input-method percentiles over the VALID same-session samples of each
 *  interval, with the invalid ones counted by reason beside them. */
export function lt2TimingSummary(): Lt2TimingSummaryRow[] {
  const byMethod = new Map<string, Lt2TimingEntry[]>();
  for (const e of _entries) {
    const list = byMethod.get(e.inputMethod) ?? [];
    list.push(e);
    byMethod.set(e.inputMethod, list);
  }
  return [...byMethod.entries()].map(([inputMethod, list]) => {
    const inputToAck = summarizeInterval(list, (c) => [c.inputAt, c.ack]);
    const requestToResponse = summarizeInterval(list, (c) => [c.request, c.response]);
    const inputToRender = summarizeInterval(list, (c) => [c.inputAt, c.render]);
    const inputToFinal = summarizeInterval(list, (c) => [c.inputAt, c.final]);
    return {
      inputMethod,
      count: list.length,
      inputToAck,
      requestToResponse,
      inputToRender,
      inputToFinal,
      inputToAckP50Ms: inputToAck.p50Ms,
      inputToAckP95Ms: inputToAck.p95Ms,
      requestToResponseP50Ms: requestToResponse.p50Ms,
      requestToResponseP95Ms: requestToResponse.p95Ms,
      inputToRenderP50Ms: inputToRender.p50Ms,
      inputToRenderP95Ms: inputToRender.p95Ms,
      inputToFinalP50Ms: inputToFinal.p50Ms,
      inputToFinalP95Ms: inputToFinal.p95Ms,
    };
  });
}

declare global {
  interface Window {
    __lt2Timing?: {
      entries: () => readonly Lt2TimingEntry[];
      summary: () => Lt2TimingSummaryRow[];
    };
  }
}

// The named-trader parity session reads this from the console; it is
// read-only and session-local.
if (typeof window !== 'undefined') {
  window.__lt2Timing = { entries: lt2TimingEntries, summary: lt2TimingSummary };
}
